import { useEffect, useState, type ReactNode } from "react";
import MaterialIcon from "@common/components/MaterialIcon";
import { displayName } from "../emulator/saves";
import ConsoleIcon from "./ConsoleIcon";
import { coverFor } from "./covers";
import { SYSTEM_ORDER, type System } from "./localLibrary";

/** A game's cover URL once known; null while loading or when it has none. */
export function useCover(name: string | null) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setSrc(null);
    if (name) coverFor(name).then((url) => live && setSrc(url));
    return () => {
      live = false;
    };
  }, [name]);
  return src;
}

/**
 * A game as the library lists it: cover, console and name, a line under
 * it, and where it lives. A row to pick (the library page) is a button; one
 * with `actions` beside it (the admin page) is not.
 */
export default function GameRow({
  name,
  system,
  meta,
  where,
  selected = false,
  onSelect,
  actions,
}: {
  name: string;
  system: string | null;
  meta: ReactNode;
  where?: ReactNode;
  selected?: boolean;
  onSelect?: () => void;
  actions?: ReactNode;
}) {
  const cover = useCover(name);
  const body = (
    <>
      <span className="library-thumb">{cover ? <img src={cover} alt="" loading="lazy" /> : <MaterialIcon name="videogame_asset" />}</span>
      <span className="library-item-text">
        <span className="library-item-name">
          {system && SYSTEM_ORDER.includes(system as System) && <ConsoleIcon system={system as System} named />}
          {displayName(name)}
        </span>
        <span className="muted small">{meta || " "}</span>
      </span>
      {where && (
        <span className="library-where" aria-hidden="true">
          {where}
        </span>
      )}
    </>
  );
  return (
    <li>
      {onSelect ? (
        <button type="button" className={`library-item${selected ? " selected" : ""}`} onClick={onSelect} aria-current={selected}>
          {body}
        </button>
      ) : (
        <div className="library-item static">
          {body}
          {actions && <span className="btn-group library-actions">{actions}</span>}
        </div>
      )}
    </li>
  );
}
